import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BlockerBadge } from "./BlockerControl";
import { DollarSign, CheckCircle2, XCircle, Loader2, ChevronDown } from "lucide-react";

export const aud = (n) => (n == null ? "—" : Number(n).toLocaleString("en-AU", { style: "currency", currency: "AUD" }));
export const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

export const ACCOUNT_STATUS_STYLES = {
  Requested: { backgroundColor: "#E8EDF4", color: "#0A1F44" },
  Included: { backgroundColor: "#F3F4F6", color: "#6B7280" },
  Quoted: { backgroundColor: "#FEF3C7", color: "#B45309" },
  Approved: { backgroundColor: "#DCFCE7", color: "#10B981" },
  Declined: { backgroundColor: "#F3F4F6", color: "#9CA3AF" },
  Invoiced: { backgroundColor: "#FEF0E6", color: "#F97316" },
  Paid: { backgroundColor: "#DCFCE7", color: "#059669" },
};

export function AccountStatusPill({ status }) {
  return (
    <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={ACCOUNT_STATUS_STYLES[status] || {}}>
      {status}
    </span>
  );
}

export function VariationDetail({ r, muted }) {
  if (r.kind !== "variation" && !r.reason) return null;
  const bits = [];
  if (r.hours != null) bits.push(`${r.hours} hrs`);
  if (r.rate != null) bits.push(`@ ${aud(r.rate)}/hr`);
  if (r.actual_hours != null) bits.push(`actual ${r.actual_hours} hrs`);
  return (
    <p className={`text-xs mt-0.5 ${muted ? "opacity-70" : ""}`} style={{ color: "#9CA3AF" }}>
      {r.kind === "variation" ? "Variation" : ""}{bits.length ? ` · ${bits.join(" ")}` : ""}{r.reason ? ` · ${r.reason}` : ""}
    </p>
  );
}

export function AccountTotals({ totals, dark }) {
  const items = [["Total", totals.total], ["Paid", totals.paid], ["Outstanding", totals.outstanding], ["Scheduled", totals.scheduled]];
  return (
    <div data-testid="account-totals" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
      {items.map(([label, val]) => (
        <div key={label} className="rounded-lg border p-3"
          style={dark ? { backgroundColor: "#0A1F44", borderColor: "#1E3A8A" } : { borderColor: "#E5E7EB", backgroundColor: "#F9FAFB" }}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}>{label}</p>
          <p data-testid={`account-total-${label.toLowerCase()}`} className={`font-heading font-bold mt-0.5 ${dark ? "text-white" : ""}`}>{aud(val)}</p>
        </div>
      ))}
    </div>
  );
}

export function RowExpander({ label, children, dark, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button data-testid={`${testId}-toggle`} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#F97316" }}>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} /> {label}
      </button>
      {open && (
        <p data-testid={`${testId}-content`}
          className={`text-xs mt-1 rounded-lg p-2 whitespace-pre-line leading-relaxed ${dark ? "text-[#cbd5e1]" : "text-[#374151]"}`}
          style={dark ? { backgroundColor: "#0A1F44" } : { backgroundColor: "#F9FAFB" }}>
          {children}
        </p>
      )}
    </div>
  );
}

function decisionIcon(busy, approve) {
  if (busy) return <Loader2 size={14} className="animate-spin" />;
  if (approve) return <CheckCircle2 size={15} />;
  return <XCircle size={15} />;
}

function DecisionModal({ row, action, onClose, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = action === "approve";

  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${row.client_id || row.clientId}/account/${row.id}/decision`, { action, name });
      toast.success(approve ? "Quote approved" : "Quote declined");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record decision");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(10,31,68,0.7)" }}>
      <div className="rounded-2xl w-full max-w-sm p-6 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }} data-testid="account-decision-modal">
        <h3 className="font-heading text-white text-lg font-bold">{approve ? "Approve" : "Decline"} this quote?</h3>
        <p className="text-sm text-[#9CA3AF] mt-1 mb-1">{row.item}</p>
        <p className="font-heading text-white text-xl font-bold mb-4">{aud(row.amount)}</p>
        <input data-testid="account-decision-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Your name (recorded with this decision)"
          className="w-full rounded-lg px-3 py-2.5 text-white outline-none text-sm mb-4"
          style={{ backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" }} />
        <div className="flex gap-2">
          <button data-testid="account-decision-cancel" onClick={onClose}
            className="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-[#9CA3AF] border" style={{ borderColor: "#1E3A8A" }}>Cancel</button>
          <button data-testid="account-decision-confirm" onClick={submit} disabled={busy}
            className="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
            style={{ backgroundColor: approve ? "#10B981" : "#EF4444" }}>
            {decisionIcon(busy, approve)}
            {approve ? "Approve" : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowByline({ r }) {
  if (r.status === "Approved" && r.approved_by) {
    return <p className="text-xs mt-0.5" style={{ color: "#10B981" }}>Approved by {r.approved_by}, {fmtD(r.approved_at)}</p>;
  }
  if (r.status === "Declined" && r.declined_by) {
    return <p className="text-xs mt-0.5 text-[#9CA3AF]">Declined by {r.declined_by}, {fmtD(r.declined_at)}</p>;
  }
  return null;
}

function QuoteDecisionButtons({ r, clientId, readOnly, onDecision }) {
  if (readOnly || r.status !== "Quoted") return null;
  return (
    <div className="flex gap-1">
      <button data-testid={`account-approve-${r.id}`} onClick={() => onDecision({ row: { ...r, clientId }, action: "approve" })}
        className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white" style={{ backgroundColor: "#10B981" }}>Approve</button>
      <button data-testid={`account-decline-${r.id}`} onClick={() => onDecision({ row: { ...r, clientId }, action: "decline" })}
        className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white" style={{ backgroundColor: "#EF4444" }}>Decline</button>
    </div>
  );
}

function ClientAccountRow({ r, clientId, readOnly, onDecision }) {
  const muted = r.status === "Included" || r.status === "Declined";
  return (
    <div data-testid={`client-account-row-${r.id}`}
      className={`grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 items-center min-w-[640px] ${muted ? "opacity-50" : ""}`}
      style={{ borderColor: "#1E3A8A" }}>
      <div className="col-span-4">
        <p className="text-white text-sm font-medium flex items-center gap-2 flex-wrap">
          {r.item}
          {r.blocker && <BlockerBadge iconOnly note={r.blocker_note} testId={`review-account-blocker-${r.id}`} />}
        </p>
        {r.blocker && r.blocker_note && (
          <p data-testid={`review-account-blocker-note-${r.id}`} className="text-xs mt-1 font-semibold" style={{ color: "#ffe606" }}>
            Blocker: {r.blocker_note}
          </p>
        )}
        <VariationDetail r={r} muted={muted} />
        {r.feedback_text && (
          <RowExpander label="View feedback" testId={`view-feedback-${r.id}`} dark>{r.feedback_text}</RowExpander>
        )}
        {r.proposed_work && (
          <RowExpander label="Proposed work" testId={`proposed-work-${r.id}`} dark>{r.proposed_work}</RowExpander>
        )}
        <RowByline r={r} />
      </div>
      <div className="col-span-2 text-sm text-white">{aud(r.amount)}</div>
      <div className="col-span-1 text-xs text-[#9CA3AF]">{r.invoice_number || "—"}</div>
      <div className="col-span-1 text-xs text-[#9CA3AF]">{fmtD(r.invoice_issued)}</div>
      <div className="col-span-1 text-xs text-[#9CA3AF]">{fmtD(r.due_date)}</div>
      <div className="col-span-1 text-xs text-[#9CA3AF]">{fmtD(r.paid_date)}</div>
      <div className="col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
        <AccountStatusPill status={r.status} />
        <QuoteDecisionButtons r={r} clientId={clientId} readOnly={readOnly} onDecision={onDecision} />
      </div>
    </div>
  );
}

export function ClientAccount({ clientId, readOnly = false }) {
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState(null); // {row, action}

  const load = () => api.get(`/clients/${clientId}/account`).then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (!data) return null;
  const { rows, totals } = data;

  return (
    <div data-testid="client-account">
      <h2 className="font-heading text-white text-2xl font-bold flex items-center gap-2">
        <DollarSign size={20} style={{ color: "#F97316" }} /> Billing
      </h2>
      <p className="text-[#9CA3AF] text-sm mt-1 mb-5">
        Every dollar in the engagement — quoted phases and variations. Work does not begin on a variation until you approve it here.
      </p>

      <div className="rounded-xl border overflow-x-auto" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] border-b min-w-[640px]" style={{ borderColor: "#1E3A8A" }}>
          <div className="col-span-4">Item</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-1">Invoice</div>
          <div className="col-span-1">Issued</div>
          <div className="col-span-1">Due</div>
          <div className="col-span-1">Paid</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {rows.length === 0 && <p className="text-sm text-[#9CA3AF] px-4 py-6 text-center">Nothing on billing yet.</p>}
        {rows.map((r) => (
          <ClientAccountRow key={r.id} r={r} clientId={clientId} readOnly={readOnly} onDecision={setDecision} />
        ))}
      </div>

      <AccountTotals totals={totals} dark />

      {decision && (
        <DecisionModal row={decision.row} action={decision.action}
          onClose={() => setDecision(null)} onDone={load} />
      )}
    </div>
  );
}
